import {
  httpsCallable,
  getFunctions,
  connectFunctionsEmulator,
} from "firebase/functions";
import { getAuth } from "firebase/auth";
import { app, crashlytics } from "./firebase";
import type { MatchProposal } from "../types";
import {
  USE_EMULATOR,
  EMULATOR_HOST,
  EMULATOR_PORTS,
} from "../config/emulator";

type MatchSuggestionResponse = {
  status: "created" | "skipped";
  reason?: string;
  proposal?: MatchProposal;
};

type MatchSuggestionRequest = {
  idToken: string;
};

const MATCH_SUGGESTIONS_FUNCTION_NAME = "generateMatchSuggestions";

export const fetchForYouMatchProposal =
  async (): Promise<MatchProposal | null> => {
    if (!app) return null;

    const auth = getAuth(app);
    if (!auth.currentUser) {
      console.warn("Match proposal call skipped: no authenticated user.");
      return null;
    }

    try {
      const functions = getFunctions(app, "asia-southeast1");

      if (USE_EMULATOR) {
        connectFunctionsEmulator(
          functions,
          EMULATOR_HOST,
          EMULATOR_PORTS.functions,
        );
        console.log(
          `📡 Connected to Functions emulator at ${EMULATOR_HOST}:${EMULATOR_PORTS.functions}`,
        );
      }

      const callable = httpsCallable<
        MatchSuggestionRequest,
        MatchSuggestionResponse
      >(functions, MATCH_SUGGESTIONS_FUNCTION_NAME);
      // Ensure token is fresh before calling the protected function
      const idToken = await auth.currentUser.getIdToken(true);
      if (USE_EMULATOR) {
        console.log(
          `🔐 Calling ${MATCH_SUGGESTIONS_FUNCTION_NAME} with idToken (emulator)`,
          {
            uid: auth.currentUser.uid,
            idTokenPreview: `${idToken.slice(0, 12)}...${idToken.slice(-8)}`,
          },
        );
      }
      const result = await callable({ idToken });
      const data = result.data;

      if (data?.status === "created" && data.proposal) {
        return data.proposal;
      }
    } catch (error: any) {
      const errorDetails = {
        code: error?.code,
        message: error?.message,
        details: error?.details,
        stack: error?.stack,
      };

      crashlytics.logError(
        `Error fetching match proposal: ${error?.code || "unknown"} - ${error?.message || JSON.stringify(error)}`,
        "matchSuggestionService.ts",
        error,
      );

      crashlytics.logError(
        `Match proposal detailed error: ${JSON.stringify(errorDetails)}`,
        "matchSuggestionService.ts",
        error,
      );
    }
    return null;
  };
